/**
 * `GET /api/content/formulas` — shared by the formula reference in the content
 * feature (`/library/formulas`) and the practice session's "Formler för
 * uppgiften" panel. One hook means one query key
 * (`["content", "formulas", chapter | "all"]`), so the panel opens on the list
 * the reference page already fetched, and the other way round.
 */
import { useQuery } from "@tanstack/react-query";
import type { Localized } from "@shared/types";
import { api } from "./client";

/** One verified formula as `server/src/features/content/routes.ts` serves it. */
export interface FormulaDto {
  id: string;
  chapter: number;
  topic: string;
  name: Localized;
  latex: string;
  variables: { symbol: string; meaning: Localized }[];
  notes?: Localized;
}

export interface FormulasResponse {
  formulas: FormulaDto[];
}

/** The formula bank changes only when the content bank is redeployed. */
export const FORMULAS_STALE_TIME = 5 * 60_000;
export const formulasQueryKey = (chapter?: number) =>
  ["content", "formulas", chapter ?? "all"] as const;

/**
 * All verified formulas, or one chapter's when `chapter` is given. `enabled: false` holds the
 * request back (the practice panel asks only once it is opened); the reference page fetches at once.
 */
export function useFormulas(chapter?: number, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: formulasQueryKey(chapter),
    queryFn: () =>
      api.get<FormulasResponse>(
        chapter ? `/api/content/formulas?chapter=${chapter}` : "/api/content/formulas",
      ),
    staleTime: FORMULAS_STALE_TIME,
  });
}
